import { bool, func, shape, string } from "prop-types";
import React, { useCallback, useEffect } from "react";
import { Button, Modal, Text, ButtonGroup, Select } from "@chaser/components";
import usePush from "../../../../../hooks/usePush";
import useShortCode from "../../../../../hooks/useShortCode";
import SettingsService from "../../../../../services/settings-service";
import useFetch, { useCacheActions } from "../../../../../hooks/useFetch";
import TaskService from "../../../../../services/task-service";
import ScheduleService from "../../../../../services/schedule-service";
import useBeAbleToStartChase from "../../../../../hooks/useBeAbleToStartChase";
import useModuleInfo from "../../../../../hooks/useModuleInfo";

const AssignedTasksUserDeleteModal = ({
  open,
  close,
  user,
  refetch,
  applyOptimisticUpdate,
  onMutationStart,
  onMutationFinish,
}) => {
  const { refetchSubscriptionData } = useBeAbleToStartChase();
  const { invalidateEndpoint } = useCacheActions();
  const shortCode = useShortCode();
  const {
    data: { chaserUsers },
  } = useFetch(SettingsService.getUsers, shortCode);

  const users = chaserUsers.filter((u) => u._id !== user._id);
  const [newUserId, setNewUserId] = React.useState(users[0]?._id);

  async function refetchReassignedUsers() {
    await invalidateEndpoint(TaskService.getTasks);
    await invalidateEndpoint(ScheduleService.getSchedules);
  }

  const { execute } = usePush(
    async (shortCode, newUserId, oldUserId) => {
      await TaskService.reassignTasksAndScheduleReminders({
        oldUserId,
        newUserId,
        shortCode,
      });
      await SettingsService.deleteUser(shortCode, oldUserId);
      await refetch();
      await refetchReassignedUsers();
      await refetchSubscriptionData();
    },
    {
      errorToast: true,
      successToast: "User reassigned and deleted successfully",
    },
  );

  return (
    <Modal
      open={open}
      toggle={close}
      title="Delete user"
      body={
        <Text>
          <strong>{user.name || user.email}</strong> has active tasks assigned
          to them, please select the user who these tasks should be reassigned
          to
          <Select onChange={(e) => setNewUserId(e.target.value)}>
            {users.map((u) => (
              <option key={u._id} value={u._id}>
                {u.profile?.firstName} {u.profile?.lastName || ""}
              </option>
            ))}
          </Select>
        </Text>
      }
      footer={
        <ButtonGroup>
          <Button
            intent="danger"
            onClick={async () => {
              onMutationStart?.();

              const apiAction = execute(shortCode, newUserId, user._id);

              applyOptimisticUpdate((state) => {
                if (state.chaserUsers) {
                  state.chaserUsers = state.chaserUsers.filter(
                    (u) => u._id !== user._id,
                  );
                }
                if (state.invitedChaserUsers) {
                  state.invitedChaserUsers = state.invitedChaserUsers.filter(
                    (u) => u.email !== user.email,
                  );
                }
              }, apiAction);

              close(false);

              try {
                const [err] = await apiAction;
                if (!err) {
                  refetch();
                }
              } finally {
                onMutationFinish?.();
              }
            }}
          >
            Yes, delete
          </Button>
          <Button appearance="secondary" onClick={() => close(false)}>
            Cancel
          </Button>
        </ButtonGroup>
      }
    />
  );
};

const DeleteUserModal = ({
  open,
  close,
  user,
  refetch,
  applyOptimisticUpdate,
  onMutationStart,
  onMutationFinish,
}) => {
  const ShortCode = useShortCode();
  const { refetchSubscriptionData } = useModuleInfo();
  const { execute, error, reset } = usePush(
    user.verified
      ? SettingsService.deleteUser
      : SettingsService.deleteUnverifiedUser,
    {
      errorToast: true,
    },
  );

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onDelete = useCallback(async () => {
    onMutationStart?.();

    const apiAction = execute(ShortCode, user.verified ? user._id : user.email);

    applyOptimisticUpdate((state) => {
      if (user.verified && state.chaserUsers) {
        state.chaserUsers = state.chaserUsers.filter((u) => u._id !== user._id);
      } else if (!user.verified && state.invitedChaserUsers) {
        state.invitedChaserUsers = state.invitedChaserUsers.filter(
          (u) => u.email !== user.email,
        );
      }
    }, apiAction);

    close(false);

    try {
      const [err] = await apiAction;
      if (!err) {
        await refetch();
        await refetchSubscriptionData();
      }
    } finally {
      onMutationFinish?.();
    }
  }, [
    ShortCode,
    close,
    execute,
    onMutationFinish,
    onMutationStart,
    refetch,
    refetchSubscriptionData,
    user._id,
    user.email,
    user.verified,
    applyOptimisticUpdate,
  ]);

  return (
    <Modal
      open={open}
      toggle={close}
      title="Delete user"
      body={
        <Text>
          Are you sure you want to delete{" "}
          <strong>{user.name || user.email}</strong>?
        </Text>
      }
      footer={
        <ButtonGroup>
          {error && <Text color="Red">{error}</Text>}
          <Button
            data-testid="delete-invite"
            data-trackingid="delete-invite"
            intent="danger"
            onClick={onDelete}
          >
            Yes, delete
          </Button>
          <Button appearance="secondary" onClick={() => close(false)}>
            Cancel
          </Button>
        </ButtonGroup>
      }
    />
  );
};

const noop = async () => {};

const DeleteUser = ({
  open,
  close,
  user,
  refetch,
  applyOptimisticUpdate,
  onMutationStart,
  onMutationFinish,
}) => {
  const shortCode = useShortCode();
  const { data: hasAssignedTasks } = useFetch(
    !user.verified ? noop : TaskService.checkTasksAndScheduleRemindersForUsers,
    {
      shortCode,
      userId: user._id,
    },
  );

  const Comp = hasAssignedTasks
    ? AssignedTasksUserDeleteModal
    : DeleteUserModal;
  return (
    <Comp
      open={open}
      close={close}
      user={user}
      refetch={refetch}
      applyOptimisticUpdate={applyOptimisticUpdate}
      onMutationStart={onMutationStart}
      onMutationFinish={onMutationFinish}
    />
  );
};

DeleteUser.propTypes = {
  open: bool.isRequired,
  close: func.isRequired,
  refetch: func,
  onMutationStart: func,
  onMutationFinish: func,
  user: shape({
    _id: string,
    email: string,
    verified: bool,
    name: string,
  }),
};

export default DeleteUser;
